/**
 * EntityFilterBar Component
 * Reusable search + filter bar for entity tables (labels, policies, SITs, etc.).
 * The filter slot is generic: pass filterOptions, filterValue, and onFilterChange
 * to render a status/type/etc. dropdown.
 */

import React from 'react';
import SearchInput from './SearchInput';
import FilterSelect from './FilterSelect';

type FilterOption = { key: string; label: string };

export type EntityFilterBarProps = {
  /** Current search term */
  search: string;
  /** Callback when search changes */
  onSearchChange: (value: string) => void;
  /** Options for the filter dropdown */
  filterOptions?: FilterOption[];
  /** Currently selected filter option key */
  filterValue?: string;
  /** Callback when filter changes */
  onFilterChange?: (value: string) => void;
  /** Aria label for the filter dropdown */
  filterAriaLabel?: string;
  /** Whether to show the filter dropdown (defaults to true) */
  showFilter?: boolean;
  /** Aria label for search input */
  searchAriaLabel?: string;
  /** Placeholder text for search input */
  searchPlaceholder?: string;
  /** Additional className for styling */
  className?: string;
  /** Custom styles for search input */
  searchStyle?: React.CSSProperties;
  /** Custom styles for filter select */
  filterStyle?: React.CSSProperties;
  /** Children to render after filters (e.g., action buttons) */
  children?: React.ReactNode;
};

const FILTER_BAR_STYLES = {
  container: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
    alignItems: 'center',
  },
  searchDefault: {
    maxWidth: 360,
  },
  filterDefault: {
    minWidth: 160,
  },
};

export const EntityFilterBar: React.FC<EntityFilterBarProps> = ({
  search,
  onSearchChange,
  filterOptions,
  filterValue = 'All',
  onFilterChange = () => undefined,
  filterAriaLabel = 'Filter by status',
  showFilter = true,
  className,
  searchStyle,
  filterStyle,
  searchAriaLabel = 'Search',
  searchPlaceholder = 'Search...',
  children,
}) => {
  return (
    <div style={FILTER_BAR_STYLES.container}>
      <SearchInput
        ariaLabel={searchAriaLabel}
        placeholder={searchPlaceholder}
        value={search}
        onChange={onSearchChange}
        className={className}
        style={{ ...FILTER_BAR_STYLES.searchDefault, ...searchStyle }}
      />
      {showFilter && filterOptions && filterOptions.length > 0 && (
        <FilterSelect
          ariaLabel={filterAriaLabel}
          options={filterOptions}
          value={filterValue}
          onChange={onFilterChange}
          className={className}
          style={{ ...FILTER_BAR_STYLES.filterDefault, ...filterStyle }}
        />
      )}
      {children}
    </div>
  );
};

export default EntityFilterBar;
